import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  useToast,
  UseToastOptions,
  VStack,
  Checkbox,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { Select } from 'chakra-react-select';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useMemo, useState, useCallback } from 'react';
import { skipToken } from '@tanstack/react-query';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { UserRoleSchema } from '@duke-hq/svc-api-db/schemas/enums/UserRole.schema';

import { DUKE_DOMAIN, emailHasAllowedDomain } from '@duke-hq/shared-constants/emails';

import { useAuthAdmin } from '../../AuthProviderAdmin';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import {
  type ConsultantRoleType,
  ConsultantRoleSchema,
  ConsultantSelectOption,
  getRolesAvailable,
} from '@duke-hq/svc-api/provider/util/consultants';

interface TeamMemberFormProps {
  id?: string;
  email: string;
  first_name: string;
  last_name: string;
  role: ConsultantRoleType | null;
  region?: string | null;
  site?: string | null;
  providerId: string | number | null;
}

type SelectOption = {
  label: string;
  value: string;
};

const defaultFormState: TeamMemberFormProps = {
  email: '',
  first_name: '',
  last_name: '',
  role: null,
  region: null,
  site: null,
  providerId: null,
};

export function ProviderConsultantForm({
  providerId,
  member,
  allowLocationSelection = true,
  onClose,
}: {
  providerId: string | number;
  member?: NonNullable<RouterOutputs['providers']['user']['consultantById']>;
  allowLocationSelection?: boolean;
  onClose?: () => void;
}) {
  const [roleSelected, setRoleSelected] = useState<ConsultantRoleType | null>(null);
  const [isStateOrRegionRoleSelected, setIsStateOrRegionRoleSelected] = useState<boolean>(false);
  const [isSiteOrConsultantRoleSelected, setIsSiteOrConsultantRoleSelected] =
    useState<boolean>(false);

  const [showRoleLocationGroupSelector, setShowRoleLocationGroupSelector] =
    useState<boolean>(!member);
  const [emailToValidate, setEmailToValidate] = useState<boolean>(false);
  const [allowDukeDomain, setAllowDukeDomain] = useState<boolean>(false);

  const auth = useAuthAdmin();

  const {
    handleSubmit,
    register,
    control,
    clearErrors,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting, isLoading },
  } = useForm<TeamMemberFormProps>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: member
      ? {
          id: member.id,
          providerId: providerId ?? undefined,
          email: member?.email?.split('@')[0], // Extract the part before '@'
          first_name: member.first_name,
          last_name: member.last_name ?? '',
          role: ConsultantRoleSchema.safeParse(member?.role).data ?? null,
          region: null,
          site: null,
        }
      : {
          ...defaultFormState,
        },
  });

  const toast = useToast();
  const utilsAdmin = trpcAdmin.useUtils();
  const email = watch('email');

  const roleOptions = useMemo(() => getRolesAvailable(), []);
  const { data: emailDomainsData } =
    trpcAdmin.providers.emailDomains.getProviderEmailDomains.useQuery(
      { providerId: Number(providerId) },
      { enabled: providerId != null },
    );

  const allowedDomains = useMemo(() => {
    if (emailDomainsData != null && emailDomainsData.length > 0) {
      return emailDomainsData.filter((record) => record.isEnabled).map((record) => record.domain);
    }
    return [];
  }, [emailDomainsData]);

  const defaultDomain = useMemo(() => {
    if (emailDomainsData != null && emailDomainsData.length > 0) {
      return (
        emailDomainsData.find((record) => record.isDefault)?.domain ?? emailDomainsData[0]?.domain
      );
    }
    return undefined;
  }, [emailDomainsData]);

  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);

  const memberDomain = useMemo(() => {
    if (member?.email != null && member.email.includes('@')) {
      return member.email.split('@')[1];
    }
    return null;
  }, [member?.email]);

  const emailDomain = useMemo(() => {
    if (member?.email?.endsWith(DUKE_DOMAIN) === true) {
      return DUKE_DOMAIN;
    }
    return selectedDomain ?? memberDomain ?? defaultDomain;
  }, [member?.email, selectedDomain, memberDomain, defaultDomain]);

  const createProviderConsultant = trpcAdmin.providers.user.createConsultant.useMutation({
    onSuccess() {
      toast({ ...toastMessage });
      reset();
      void utilsAdmin.providers.user.listConsultantsWithPagination.refetch();
    },
  });

  const updateProviderConsultant = trpcAdmin.providers.user.updateConsultant.useMutation({
    onSuccess: () => {
      toast({ ...toastMessage });
      void utilsAdmin.providers.user.listConsultantsWithPagination.refetch();
      void utilsAdmin.providers.user.consultantById.refetch();
    },
  });

  const { refetch: checkEmail } = trpcAdmin.providers.user.searchConsultant.useQuery(
    email != null
      ? {
          providerId,
          email: allowDukeDomain ? `${email}@${DUKE_DOMAIN}` : `${email}@${emailDomain}`,
        }
      : skipToken,
    {
      enabled: Boolean(emailToValidate),
    },
  );

  const { data: siteRegions } = trpcAdmin.providers.locations.listSiteRegions.useQuery(
    {
      providerId,
    },
    {
      enabled: Boolean(
        roleSelected &&
        (roleSelected === ConsultantRoleSchema.enum.STATE_MANAGER ||
          roleSelected === ConsultantRoleSchema.enum.REGIONAL_MANAGER),
      ),
    },
  );

  const siteRegionsOptions = useMemo(() => {
    return siteRegions?.map((siteRegion) => ({
      label: siteRegion.name,
      value: siteRegion.id,
    }));
  }, [siteRegions]);

  const { data: siteLocations } = trpcAdmin.providers.locations.listSiteLocations.useQuery(
    {
      providerId,
      filters: {
        available: true,
      },
    },
    {
      enabled: Boolean(
        roleSelected &&
        (roleSelected === ConsultantRoleSchema.enum.SITE_MANAGER ||
          roleSelected === ConsultantRoleSchema.enum.CONSULTANT),
      ),
    },
  );

  const siteLocationsOptions = useMemo(() => {
    return siteLocations?.map((siteLocation) => ({
      label: `${siteLocation.name}, ${siteLocation.state}`,
      value: siteLocation.id,
    }));
  }, [siteLocations]);

  const toastMessage: UseToastOptions = {
    title: member ? t`Team member updated successfully.` : t`Team member invited successfully.`,
    status: 'success',
    duration: 7000,
    isClosable: true,
  };

  const validateEmail = useCallback(async () => {
    if (email == null) {
      return true;
    }

    // Skip validation if site prop is passed and name is unchanged
    if (member?.email != null && member.email.length > 0 && email === member.email.split('@')[0]) {
      return true;
    }

    setEmailToValidate(true);

    const result = await checkEmail();
    setEmailToValidate(false);

    return result.data === false;
  }, [email, member, checkEmail]);

  const handleRoleChange = useCallback(
    (role: ConsultantRoleType) => {
      setRoleSelected(role);
      setIsStateOrRegionRoleSelected(false);
      setIsSiteOrConsultantRoleSelected(false);

      if (
        role === ConsultantRoleSchema.enum.STATE_MANAGER ||
        role === ConsultantRoleSchema.enum.REGIONAL_MANAGER
      ) {
        setIsStateOrRegionRoleSelected(true);
        setIsSiteOrConsultantRoleSelected(false);

        // Clear the values when the role changes
        setValue('site', null);
      } else if (
        role === ConsultantRoleSchema.enum.SITE_MANAGER ||
        role === ConsultantRoleSchema.enum.CONSULTANT
      ) {
        setIsSiteOrConsultantRoleSelected(true);
        setIsStateOrRegionRoleSelected(false);

        // Clear the values when the role changes
        setValue('region', null);
      }
    },
    [setValue, setRoleSelected, setIsStateOrRegionRoleSelected, setIsSiteOrConsultantRoleSelected],
  );

  const handleAllowDukeDomain = useCallback(() => {
    setAllowDukeDomain((prev) => !prev);
  }, []);

  const onSubmit = async (data: TeamMemberFormProps) => {
    const isEmailValid = await validateEmail();
    if (!isEmailValid) {
      setError('email', {
        type: 'manual',
        message: `The email ${email}${
          allowDukeDomain ? `@${DUKE_DOMAIN}` : `@${emailDomain}`
        } is already taken. Please choose a unique email.`,
      });

      setEmailToValidate(false);
      return;
    }

    if (data.role == null) {
      setError('role', { type: 'manual', message: t`Please select a role` });
      return;
    }

    if (member?.id != null) {
      await updateProviderConsultant.mutateAsync({
        id: member.id,
        first_name: data.first_name,
        last_name: data.last_name,
        email: `${data.email}@${emailDomain}`,
        role: data.role,
        providerId,
      });
    } else {
      const resolvedEmail = data.email
        ? allowDukeDomain
          ? `${data.email}@${DUKE_DOMAIN}`
          : `${data.email}@${emailDomain}`
        : undefined;

      if (resolvedEmail == null) {
        setError('email', { type: 'manual', message: t`Email is required` });
        return;
      }

      await createProviderConsultant.mutateAsync({
        first_name: data.first_name,
        last_name: data.last_name,
        email: resolvedEmail,
        role: data.role,
        ...(auth?.user?.role !== UserRoleSchema.enum.ADMIN && {
          created_by: auth?.user?.id,
        }),
        region: data.region ?? undefined,
        site: data.site ?? undefined,
        providerId,
      });
    }

    if (onClose) {
      onClose();
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }} noValidate>
      <VStack spacing={4} align="stretch" p={4}>
        <FormControl isInvalid={!!errors.email} isRequired>
          <FormLabel htmlFor="ProviderConsultantForm-email">
            <Trans>Email</Trans>
          </FormLabel>
          <InputGroup>
            <Input
              id="ProviderConsultantForm-email"
              type="text"
              autoComplete="off"
              readOnly={!!member?.deleted_at}
              disabled={!!member?.deleted_at}
              borderRightRadius={!allowDukeDomain && allowedDomains.length > 1 ? 0 : undefined}
              {...register('email', {
                required: t`Email is required`,
                validate: (value?: string) => {
                  if (value?.includes('@') === true) {
                    return t`Email cannot contain the '@' symbol. Please enter only the username.`;
                  }

                  const effectiveAllowedDomains = allowDukeDomain
                    ? [...allowedDomains, DUKE_DOMAIN]
                    : allowedDomains;
                  const constructedEmail = `${value ?? ''}${allowDukeDomain ? `@${DUKE_DOMAIN}` : `@${emailDomain}`}`;
                  const isValid = emailHasAllowedDomain(effectiveAllowedDomains, constructedEmail);

                  if (isValid === false) {
                    return t`Email must match one of: ${effectiveAllowedDomains.map((domain) => `@${domain}`).join(', ')}`;
                  }

                  return true;
                },
                onChange: () => {
                  if (errors.email) {
                    clearErrors('email');
                  }
                },
              })}
            />
            {emailDomain != null ? (
              allowDukeDomain || allowedDomains.length <= 1 ? (
                <InputRightAddon flex="0 0 40%">
                  {allowDukeDomain ? `@${DUKE_DOMAIN}` : `@${emailDomain}`}
                </InputRightAddon>
              ) : (
                <Box flex="0 0 40%">
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
                    }}
                    isSearchable={false}
                    isDisabled={!!member?.deleted_at}
                    options={allowedDomains.map((domain) => ({
                      label: `@${domain}`,
                      value: domain,
                    }))}
                    value={{ label: `@${emailDomain}`, value: emailDomain ?? '' }}
                    onChange={(option) => {
                      if (option != null) {
                        setSelectedDomain(option.value);
                      }
                    }}
                    chakraStyles={{
                      control: (provided) => ({
                        ...provided,
                        borderLeftRadius: 0,
                      }),
                    }}
                  />
                </Box>
              )
            ) : null}
          </InputGroup>
          <FormErrorMessage>{errors.email?.message}</FormErrorMessage>

          {!member ? (
            <Checkbox mt={2} isChecked={allowDukeDomain} onChange={handleAllowDukeDomain}>
              <Trans>Allow duke users</Trans>
            </Checkbox>
          ) : null}
        </FormControl>

        <FormControl isRequired isInvalid={!!errors.first_name}>
          <FormLabel htmlFor="ProviderConsultantForm-first_name">
            <Trans>First name</Trans>
          </FormLabel>
          <Input
            id="ProviderConsultantForm-first_name"
            autoComplete="off"
            readOnly={!!member?.deleted_at}
            disabled={!!member?.deleted_at}
            {...register('first_name', {
              required: t`First name is required`,
              onChange: () => {
                if (errors.first_name) {
                  clearErrors('first_name');
                }
              },
            })}
          />
          <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired isInvalid={!!errors.last_name}>
          <FormLabel htmlFor="ProviderConsultantForm-last_name">
            <Trans>Last name</Trans>
          </FormLabel>
          <Input
            id="ProviderConsultantForm-last_name"
            autoComplete="off"
            readOnly={!!member?.deleted_at}
            disabled={!!member?.deleted_at}
            {...register('last_name', {
              required: t`Last name is required`,
              onChange: () => {
                if (errors.last_name) {
                  clearErrors('last_name');
                }
              },
            })}
          />
          <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired isInvalid={!!errors.role}>
          <FormLabel htmlFor="ProviderConsultantForm-role">
            <Trans>Role</Trans>
          </FormLabel>
          {showRoleLocationGroupSelector ? (
            <Controller
              name="role"
              control={control}
              defaultValue={ConsultantRoleSchema.safeParse(member?.role).data ?? null}
              render={({ field }) => (
                <Select
                  inputId="ProviderConsultantForm-role"
                  placeholder={t`Please select role`}
                  options={roleOptions}
                  value={roleOptions?.find(
                    (option: ConsultantSelectOption) => option.value === field.value,
                  )}
                  isDisabled={!!member?.deleted_at}
                  onChange={(val: ConsultantSelectOption | null) => {
                    field.onChange(val?.value);
                    if (val?.value != null && val.value != undefined) {
                      handleRoleChange(val.value);
                    }
                  }}
                />
              )}
              rules={{
                required: t`Please select a role`,
              }}
            />
          ) : (
            <InputGroup>
              <Input
                id="ProviderConsultantForm-role"
                readOnly
                value={
                  roleOptions?.find(
                    (option: ConsultantSelectOption) => option.value === member?.role,
                  )?.label
                }
              />
              <InputRightAddon>
                <Button
                  variant="link"
                  isDisabled={!!member?.deleted_at}
                  onClick={() => {
                    setShowRoleLocationGroupSelector(true);
                    setValue('role', null);
                  }}
                >
                  <Trans>Change</Trans>
                </Button>
              </InputRightAddon>
            </InputGroup>
          )}
          <FormErrorMessage>{errors.role?.message}</FormErrorMessage>
        </FormControl>

        {allowLocationSelection && roleSelected ? (
          <>
            {isStateOrRegionRoleSelected ? (
              <FormControl isRequired isInvalid={!!errors.region}>
                <FormLabel htmlFor="ProviderConsultantForm-region">
                  <Trans>Region</Trans>
                </FormLabel>
                <Controller
                  name="region"
                  control={control}
                  render={({ field }) => (
                    <Select
                      id="region"
                      inputId="ProviderConsultantForm-region"
                      placeholder={t`Please select region`}
                      options={siteRegionsOptions}
                      value={siteRegionsOptions?.find(
                        (option: SelectOption) => option.value === field.value,
                      )}
                      onChange={(val: SelectOption | null) => field.onChange(val?.value)}
                    />
                  )}
                  rules={{ required: t`Please select a region` }}
                />
                <FormErrorMessage>{errors.region?.message}</FormErrorMessage>
              </FormControl>
            ) : null}

            {isSiteOrConsultantRoleSelected ? (
              <FormControl isRequired isInvalid={!!errors.site}>
                <FormLabel htmlFor="ProviderConsultantForm-site">
                  <Trans>Site</Trans>
                </FormLabel>
                <Controller
                  name="site"
                  control={control}
                  render={({ field }) => (
                    <Select
                      id="site"
                      inputId="ProviderConsultantForm-site"
                      placeholder={t`Please select site`}
                      options={siteLocationsOptions}
                      value={siteLocationsOptions?.find(
                        (option: SelectOption) => option.value === field.value,
                      )}
                      onChange={(val: SelectOption | null) => field.onChange(val?.value)}
                    />
                  )}
                  rules={{ required: t`Please select a site` }}
                />
                <FormErrorMessage>{errors.site?.message}</FormErrorMessage>
              </FormControl>
            ) : null}
          </>
        ) : null}

        <Flex justify={'flex-end'}>
          <Button
            right={0}
            type="submit"
            isLoading={isSubmitting}
            isDisabled={
              member?.deleted_at
                ? true
                : member
                  ? updateProviderConsultant.isPending || isSubmitting || isLoading
                  : createProviderConsultant.isPending || isSubmitting || isLoading
            }
          >
            {member ? t`Update team member` : t`Invite team member`}
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
}
